import { COMPARISON_SLUGS, type ComparisonSlug } from "@listou/shared/constants";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { FaqSection, Prose, SourcesSection, TryListou, Verdict } from "../components/prose";
import { SiteFooter, SiteHeader } from "../components/site-header";
import { COMPARISONS, comparisonJsonLd } from "../lib/comparisons";
import { formatDay } from "../lib/format";
import { seo } from "../lib/head";

const isComparisonSlug = (value: string): value is ComparisonSlug =>
  (COMPARISON_SLUGS as readonly string[]).includes(value);

export const Route = createFileRoute("/comparatif/$slug")({
  staticData: { ssr: true },
  loader: ({ params }) => {
    if (!isComparisonSlug(params.slug)) throw notFound();
    return { slug: params.slug };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const comparison = COMPARISONS[loaderData.slug];
    return seo({
      title: comparison.seoTitle,
      description: comparison.seoDescription,
      jsonLd: comparisonJsonLd(comparison),
    });
  },
  component: ComparisonPage,
});

function ComparisonPage() {
  const { slug } = Route.useLoaderData();
  const comparison = COMPARISONS[slug];
  const checkedOn = formatDay(comparison.checkedOn);

  return (
    <div className="min-h-dvh">
      <SiteHeader signedIn={false} />
      <main className="mx-auto grid max-w-4xl gap-14 px-4 pt-6 pb-20 sm:px-6">
        <nav aria-label="Fil d'Ariane" className="text-muted">
          <Link to="/comparatif" className="font-bold hover:underline">
            Comparatifs
          </Link>
          <span aria-hidden> › </span>
          <span>{comparison.name}</span>
        </nav>

        <header className="grid gap-5">
          <h1 className="text-4xl font-medium sm:text-5xl">{comparison.h1}</h1>
          <p className="text-xl leading-relaxed text-ink/85">{comparison.summary}</p>
          <p className="text-sm text-muted">
            Informations sur {comparison.name} relevées sur {comparison.site} le {checkedOn}.
          </p>
        </header>

        <section className="grid gap-4">
          <h2 className="text-3xl font-semibold">Le comparatif point par point</h2>
          <div className="overflow-x-auto rounded-2xl border-[1.5px] border-line">
            <table className="w-full min-w-[36rem] border-collapse text-left">
              <caption className="sr-only">
                Listou et {comparison.name}, fonctionnalité par fonctionnalité
              </caption>
              <thead className="bg-ink/[0.04]">
                <tr>
                  <th scope="col" className="w-1/4 p-4 font-bold">
                    Fonctionnalité
                  </th>
                  <th scope="col" className="p-4 font-bold">
                    Listou
                  </th>
                  <th scope="col" className="p-4 font-bold">
                    {comparison.name}
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparison.rows.map((row) => (
                  <tr key={row.feature} className="border-t border-line align-top">
                    <th scope="row" className="p-4 font-bold">
                      {row.feature}
                    </th>
                    <td className="p-4">
                      <Verdict cell={row.listou} />
                    </td>
                    <td className="p-4">
                      <Verdict cell={row.them} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <Prose>{comparison.body}</Prose>

        <section className="grid gap-6 sm:grid-cols-2">
          <div className="panel grid content-start gap-3 p-6">
            <h2 className="text-2xl font-semibold">Choisissez {comparison.name} si…</h2>
            <ul className="grid list-disc gap-2 pl-5 leading-relaxed text-ink/85">
              {comparison.chooseThem.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </div>
          <div className="panel grid content-start gap-3 p-6">
            <h2 className="text-2xl font-semibold">Choisissez Listou si…</h2>
            <ul className="grid list-disc gap-2 pl-5 leading-relaxed text-ink/85">
              {comparison.chooseListou.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </div>
        </section>

        <FaqSection faq={comparison.faq} />

        <TryListou />

        <SourcesSection
          intro={`Chaque information sur ${comparison.name} vient de son site, consulté le ${checkedOn}. Une erreur ou un changement ? Écrivez-nous, nous corrigeons.`}
          sources={comparison.sources}
        />
      </main>
      <SiteFooter />
    </div>
  );
}
